import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2 } from "lucide-react";
import api from "../services/api";
import CustomerImportBatchStatusBadge from "../components/customerImportHistory/CustomerImportBatchStatusBadge";
import CustomerImportBatchSummaryCards from "../components/customerImportHistory/CustomerImportBatchSummaryCards";
import CustomerImportBatchErrorsTable from "../components/customerImportHistory/CustomerImportBatchErrorsTable";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL");
}

export default function CustomerImportBatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [batch, setBatch] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function loadBatch() {
      try {
        setLoading(true);
        setError("");
        const res = await api.get(`/clients/import/batches/${id}`);
        if (!cancelled) setBatch(res.data || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || "Batch detail laden mislukt.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadBatch();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-4 md:p-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-slate-900 p-2 text-white"><Building2 size={18} /></div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Logboek24</p>
                <h1 className="text-2xl font-semibold text-slate-900">Importbatch detail</h1>
              </div>
            </div>
            <button type="button" onClick={() => navigate("/klanten/import/historie")} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">Terug naar historie</button>
          </div>
        </header>

        {loading && <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600">Batch laden...</div>}
        {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">{error}</div>}

        {!loading && !error && batch && (
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="grid gap-2 text-sm text-slate-700 md:grid-cols-2">
                <p><span className="text-slate-500">Batch-id:</span> {batch.importBatchId || batch._id}</p>
                <p><span className="text-slate-500">Bronprofiel:</span> {batch.sourceType || "-"}</p>
                <p><span className="text-slate-500">Bestandsnaam:</span> {batch.filename || "-"}</p>
                <p><span className="text-slate-500">Strategie:</span> {batch.strategy || "-"}</p>
                <p><span className="text-slate-500">Datum/tijd:</span> {formatDate(batch.createdAt)}</p>
                <p><span className="text-slate-500">Gebruiker:</span> {batch.createdBy?.name || batch.createdBy?.email || "-"}</p>
                <p><span className="text-slate-500">Status:</span> <CustomerImportBatchStatusBadge status={batch.status} /></p>
                <p><span className="text-slate-500">Preview-id:</span> {batch.previewId || "-"}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-slate-900">Samenvatting</h3>
              <CustomerImportBatchSummaryCards summary={batch.summary} />
            </div>

            <CustomerImportBatchErrorsTable errors={batch.errors} />

            {Array.isArray(batch.createdClients) && batch.createdClients.length > 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <h3 className="mb-2 text-sm font-semibold text-slate-900">Aangemaakte klanten</h3>
                <ul className="list-disc pl-4 text-sm text-slate-700">
                  {batch.createdClients.slice(0, 50).map((item) => (
                    <li key={item.id || item.naam}>{item.naam || item.id}</li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
